import {
  ProviderProps,
  ChangeEvent,
  ReactElement,
  createContext,
  useContext,
  useCallback,
} from 'react';

import { ItemsSearchFilters } from '~hooks/useItemsSearchFilters';

import {
  OrdersFilterMenuGiverCurrentFiltersAutocompleteRefs,
  OrdersFilterMenuGiverCurrentFilters,
  OrdersFilterMenuGiverContextValue,
  OrdersFilterMenuGiverProviderProps,
} from './types';

const DEFAULT_FILTER_AUTOCOMPLETE_REFS: OrdersFilterMenuGiverCurrentFiltersAutocompleteRefs =
  {
    itemState: { current: null },
    sourceImports: { current: null },
  };

const DEFAULT_FILTER_STATE: ItemsSearchFilters = {
  // COMMONS
  search: '',
  expirationDateTimeFrom: null,
  expirationDateTimeTo: null,
  includeWithoutExpirationDate: null,
  dateFrom: null,
  dateTo: null,
  // SPECIFIC ITEMS
  finalizedDateTimeFrom: null,
  finalizedDateTimeTo: null,
  unit1Gt: '',
  unit1Lt: '',
  unit2Gt: '',
  unit2Lt: '',
  priceGt: '',
  priceLt: '',
  vehicles: null,
  drivers: null,
  services: null,
  zones: null,
  assignedUser: null,
  itemState: null,
  pickingState: null,
  sourceImports: null,
  // SPECIFIC COLLECT
  giverCompany: null,
  collectState: null,
  giverCollectState: null,
};

const DEFAULT_FILTER: OrdersFilterMenuGiverCurrentFilters = {
  autocompletesRefs: DEFAULT_FILTER_AUTOCOMPLETE_REFS,
  filters: DEFAULT_FILTER_STATE,
  activeFilters: DEFAULT_FILTER_STATE,
  onChangeFilterInput: () => {},
  onResetFilters: () => {},
  onApplyFilters: () => {},
  onClearFilters: () => {},
};

const OrdersFilterMenuGiverContext =
  createContext<OrdersFilterMenuGiverContextValue>({
    type: 'inPreparation',
    currentFilters: DEFAULT_FILTER,
    handleChangeInputFilter: () => () => {},
    handleChangeInputDateFilter: () => () => () => {},
    handleManualClose: () => {},
  });

export const useOrdersFilterMenuGiverContext =
  (): OrdersFilterMenuGiverContextValue =>
    useContext(OrdersFilterMenuGiverContext);

const OrdersFilterMenuGiverProvider = ({
  value,
  children,
}: ProviderProps<OrdersFilterMenuGiverProviderProps>): ReactElement => {
  const handleChangeInputFilter = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value: inputValue } = event.target ?? event.currentTarget;

      value.currentFilters.onChangeFilterInput(name, inputValue);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value.currentFilters.onChangeFilterInput],
  );

  const handleChangeInputDateFilter = useCallback(
    (fieldName: string) => (inputValue: Date | null) => {
      value.currentFilters.onChangeFilterInput(fieldName, inputValue);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value.currentFilters.onChangeFilterInput],
  );

  return (
    <OrdersFilterMenuGiverContext.Provider
      value={{
        ...value,
        handleChangeInputFilter,
        handleChangeInputDateFilter,
      }}
    >
      {children}
    </OrdersFilterMenuGiverContext.Provider>
  );
};

export default OrdersFilterMenuGiverProvider;
